import { AnimatePresence, motion } from "framer-motion";
import { ShieldAlert, X } from "lucide-react";
import { useEffect } from "react";

/**
 * Animated, themed anti-cheat violation popup.
 *
 * Renders a top-center floating banner with neon glow + clear violation label.
 * Auto-dismisses after `autoCloseMs` (default 2400ms). The caller decides what
 * action to take on the violation (forfeit, mark wrong, etc) — this is pure
 * UI feedback.
 *
 * Violation map → label:
 *   - "tab_switch": "Cannot switch tabs"
 *   - "paste":      "Cannot copy paste"
 *   - "copy":       "Cannot copy paste"
 *   - "forfeit":    "Round forfeited — violation detected"
 *   - other:        custom message via `message` prop
 */
const LABELS = {
  tab_switch: "Cannot switch tabs",
  paste:      "Cannot copy paste",
  copy:       "Cannot copy paste",
  forfeit:    "Round forfeited",
};

const DETAILS = {
  tab_switch: "Switching tabs or windows during a question is not allowed.",
  paste:      "Pasting is disabled. Type your answer manually.",
  copy:       "Copying question text is disabled.",
  forfeit:    "Anti-cheat violation — this round was awarded to your opponent.",
};

export default function AntiCheatPopup({ violation, message, visible, onDismiss, autoCloseMs = 2400 }) {
  const label = message || LABELS[violation] || "Violation detected";
  const detail = (!message && DETAILS[violation]) || "";

  useEffect(() => {
    if (!visible || !autoCloseMs) return;
    const t = setTimeout(() => onDismiss?.(), autoCloseMs);
    return () => clearTimeout(t);
  }, [visible, autoCloseMs, onDismiss, violation]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="ac-popup"
          initial={{ opacity: 0, y: -30, scale: 0.92 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{ type: "spring", stiffness: 280, damping: 22 }}
          className="fixed top-6 left-1/2 -translate-x-1/2 z-[150] w-[min(92vw,460px)]"
          data-testid="anticheat-popup"
          data-violation={violation || "unknown"}
        >
          <div
            className="relative rounded-2xl border px-5 py-4 flex items-start gap-3 backdrop-blur-md"
            style={{
              background: "linear-gradient(135deg, rgba(35,8,20,0.92) 0%, rgba(20,5,12,0.96) 100%)",
              borderColor: "rgba(244,63,94,0.65)",
              boxShadow: "0 0 36px rgba(244,63,94,0.45), inset 0 0 20px rgba(244,63,94,0.08)",
            }}
          >
            <motion.div
              animate={{ scale: [1, 1.12, 1] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
              className="shrink-0 w-10 h-10 rounded-xl grid place-items-center"
              style={{
                background: "rgba(244,63,94,0.15)",
                border: "1px solid rgba(244,63,94,0.55)",
                boxShadow: "0 0 16px rgba(244,63,94,0.4)",
              }}
            >
              <ShieldAlert size={18} className="text-rose-300" />
            </motion.div>
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[10px] tracking-[0.35em] text-rose-300 uppercase">
                Anti-Cheat
              </div>
              <div className="font-heading font-bold text-lg leading-tight text-white mt-0.5">
                {label}
              </div>
              {detail && (
                <div className="text-soft text-xs mt-1 leading-snug">{detail}</div>
              )}
            </div>
            <button
              onClick={onDismiss}
              className="text-faint hover:text-white p-1 -mr-1"
              aria-label="Dismiss"
              data-testid="anticheat-popup-dismiss"
            >
              <X size={14} />
            </button>
            {/* Bottom progress shrink (countdown) */}
            <motion.div
              key={`bar-${violation}`}
              initial={{ width: "100%" }}
              animate={{ width: "0%" }}
              transition={{ duration: autoCloseMs / 1000, ease: "linear" }}
              className="absolute left-3 right-3 bottom-1 h-0.5 rounded-full"
              style={{ background: "linear-gradient(90deg, #F43F5E, #BE123C)" }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
